// BUILD-042 (Oct 16, clarified Oct 17) - the values HR types when assigning a form that has HR-entered fields (expanded fields).
// These are frozen with the assignment: they are checked by the server before the assignment exists, never change afterwards, and are
// never shown back in lists. Only what HR is asked for appears in the inputs; the employee's own answers are never part of this.
//
// Oct 17 clarification: HR sees, side by side, (1) what HR fills in NOW, (2) what the EMPLOYEE will fill in while signing (named only,
// never asked for here), and (3) what is copied from the employee record. The two nine-digit formats are told apart in words, so a
// bank routing number is never confused with a Social Security number.
import type { HrAssignmentPrompt, HrAssignmentSummary } from '../../api/client';
import { DateEntry } from './DateEntry';

/** Plain-language hint for the formats that are easy to mix up. Null for everything else. */
export function formatHint(validation: string | null): string | null {
  if (validation === 'routing9') return 'Bank routing number: exactly 9 digits. This is not a Social Security number.';
  if (validation === 'ssnUS') return 'Social Security number: 9 digits, for example 123-45-6789. This is not a bank routing number.';
  // dateUS has its own control (DateEntry) with its own format line
  return null;
}

export function AssignValueInputs({
  prompts, values, onChange, idPrefix, summary,
}: {
  prompts: HrAssignmentPrompt[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  idPrefix: string;
  summary?: HrAssignmentSummary | null;
}) {
  const employeeSupplies = summary?.employeeSupplies ?? [];
  const recordSupplies = summary?.recordSupplies ?? [];
  if (!prompts.length && !employeeSupplies.length && !recordSupplies.length && !(summary?.fixedSupplies?.length)) return null;
  return (
    <div className="w-full mt-1 space-y-1.5" data-testid="assign-values">
      {prompts.length > 0 && (
        <fieldset className="rounded border border-[#4AABDE]/40 bg-white p-2 space-y-1.5">
          <legend className="px-1 text-[11px] font-semibold text-gray-700">HR fills in now</legend>
          <p className="text-[11px] text-gray-500">HR enters these when assigning the form. The employee sees them read-only and cannot change them. They are saved with this assignment and cannot be changed afterwards.</p>
          {prompts.map((p) => {
            const id = `${idPrefix}-${p.fieldKey}`;
            const hintId = `${id}-hint`;
            const v = values[p.fieldKey] ?? '';
            const hint = formatHint(p.validation);
            return (
              <div key={p.fieldKey} className="flex flex-col gap-0.5">
                <label htmlFor={id} className="text-[11px] font-medium text-gray-700">{p.label}{p.required ? <span aria-hidden="true"> *</span> : <span className="text-gray-400"> (optional)</span>}</label>
                {p.kind === 'dropdown' && p.options ? (
                  <select id={id} value={v} onChange={(e) => onChange({ ...values, [p.fieldKey]: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" data-testid={`assign-value-${p.fieldKey}`}>
                    <option value="">Choose…</option>
                    {p.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : p.validation === 'dateUS' ? (
                  <DateEntry id={id} value={v} onChange={(next) => onChange({ ...values, [p.fieldKey]: next })} testId={`assign-value-${p.fieldKey}`} />
                ) : (
                  <input id={id} value={v} maxLength={p.maxLength ?? 1000} autoComplete="off" spellCheck={false}
                    aria-describedby={hint ? hintId : undefined}
                    inputMode={p.validation === 'numbers' || p.validation === 'currency' || p.validation === 'routing9' || p.validation === 'ssnUS' || p.validation === 'zipUS' ? 'numeric' : undefined}
                    onChange={(e) => onChange({ ...values, [p.fieldKey]: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" data-testid={`assign-value-${p.fieldKey}`} />
                )}
                {hint && <span id={hintId} className="text-[11px] text-gray-500" data-testid={`assign-hint-${p.fieldKey}`}>{hint}</span>}
              </div>
            );
          })}
        </fieldset>
      )}
      {employeeSupplies.length > 0 && (
        <div className="rounded border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-600" data-testid="assign-employee-supplies">
          <div className="font-semibold text-gray-700">The employee fills in while signing (nothing to enter here)</div>
          <ul className="list-disc pl-4 mt-0.5">
            {employeeSupplies.map((s) => <li key={s.label}>{s.label}{s.required ? '' : ' (optional)'}{s.startsWithAnswer ? ' — starts with an answer the employee can change' : ''}</li>)}
          </ul>
        </div>
      )}
      {(summary?.fixedSupplies?.length ?? 0) > 0 && (
        <div className="rounded border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-600" data-testid="assign-fixed-supplies">
          <div className="font-semibold text-gray-700">Fixed text set in the form (read-only for the employee)</div>
          <ul className="list-disc pl-4 mt-0.5">{summary!.fixedSupplies!.map((s) => <li key={s.label}>{s.label}</li>)}</ul>
        </div>
      )}
      {recordSupplies.length > 0 && (
        <div className="rounded border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-600" data-testid="assign-record-supplies">
          <div className="font-semibold text-gray-700">Copied from the employee record (nothing to enter here; read-only for the employee)</div>
          <ul className="list-disc pl-4 mt-0.5">
            {recordSupplies.map((s) => <li key={s.label}>{s.label}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
